import type { Tracker } from '@archipro-website/tracker';
import type { BulkEnquiryItem } from './bulk-enquiry-state';

/**
 * Funnel events for bulk enquiry, which previously emitted none of its own.
 *
 * The flow is `enter mode → add items → review → form → submit → sent`, and
 * only the last two were visible — through the shared enquiry events, which
 * fire once per submission and so cannot tell a fifty-recipient bulk send from
 * a single enquiry. These cover the steps before it, so the drop-offs can be
 * seen: entered but never added, added but never opened, opened but never sent,
 * and explicitly discarded.
 *
 * Adds and removes are reported from `useTrackedBulkEnquiryToggle`, which both
 * the tile click and the checkbox go through. The tile reaches the selection
 * write-only so the grid does not re-render on every toggle, so that helper
 * reads the store imperatively rather than subscribing — instrumenting only the
 * subscribing hook missed the highest-volume add in the funnel entirely.
 *
 * Every payload travels in `Source` and `ExtraData`; no other custom key
 * survives the warehouse pipeline.
 */

/** The browse page bulk mode was started from. */
export type BulkEnquiryEntityType = 'products' | 'professionals' | 'projects';

const BULK_ENQUIRY_SOURCE = 'bulkEnquiry';

const logBulkEvent = (
    event:
        | 'BulkEnquiryModeEntered'
        | 'BulkEnquiryItemAdded'
        | 'BulkEnquiryItemRemoved'
        | 'BulkEnquiryReviewOpened'
        | 'BulkEnquiryFormOpened'
        | 'BulkEnquiryDiscarded',
    tracker: Tracker,
    data: Record<string, unknown>,
): void => {
    void tracker.log(event, {
        url: new URL(window.location.href),
        data: {
            ...data,
            Source: BULK_ENQUIRY_SOURCE,
            ExtraData: JSON.stringify(data),
        },
    });
};

/**
 * Bulk mode turned on: checkboxes replace the save hearts. `entityType` is the
 * browse page it started from, which is what says where the feature earns its
 * keep.
 */
export const trackBulkEnquiryModeEntered = (
    tracker: Tracker,
    { entityType }: { entityType: BulkEnquiryEntityType },
): void => logBulkEvent('BulkEnquiryModeEntered', tracker, { entityType });

/**
 * A tile was added to the selection. `itemCount` and `supplierCount` are the
 * totals *after* the change, so a session's churn can be read without
 * replaying every event.
 */
export const trackBulkEnquiryItemAdded = (
    tracker: Tracker,
    {
        item,
        itemCount,
        supplierCount,
    }: { item: BulkEnquiryItem; itemCount: number; supplierCount: number },
): void =>
    logBulkEvent('BulkEnquiryItemAdded', tracker, {
        itemType: item.itemType,
        siteTreeID: item.siteTreeID,
        targetProfessionalId: item.targetProfessionalId,
        itemCount,
        supplierCount,
    });

/** A tile was taken back out. Totals are again the state after the change. */
export const trackBulkEnquiryItemRemoved = (
    tracker: Tracker,
    {
        siteTreeID,
        itemCount,
        supplierCount,
    }: { siteTreeID: number; itemCount: number; supplierCount: number },
): void =>
    logBulkEvent('BulkEnquiryItemRemoved', tracker, {
        siteTreeID,
        itemCount,
        supplierCount,
    });

/** The modal opened on its review step. */
export const trackBulkEnquiryReviewOpened = (
    tracker: Tracker,
    { itemCount, supplierCount }: { itemCount: number; supplierCount: number },
): void =>
    logBulkEvent('BulkEnquiryReviewOpened', tracker, {
        itemCount,
        supplierCount,
    });

/**
 * Review passed; the enquiry form is now on screen.
 *
 * Also the bulk send's `EnquiryReveal`, so the enquiry funnel that every other
 * CTA reports into counts it too. Its `Source` is `BulkEnquiry`, the value the
 * submit already carries, so a reveal and its lead line up by surface.
 */
export const trackBulkEnquiryFormOpened = (
    tracker: Tracker,
    { itemCount, supplierCount }: { itemCount: number; supplierCount: number },
): void => {
    logBulkEvent('BulkEnquiryFormOpened', tracker, {
        itemCount,
        supplierCount,
    });
    void tracker.log('EnquiryReveal', {
        url: new URL(window.location.href),
        data: {
            Source: 'BulkEnquiry',
            CTA_Label: 'Bulk enquiry',
            ExtraData: JSON.stringify({ itemCount, supplierCount }),
        },
        targetTracker: ['archiproTracker', 'ga4Tracker'],
    });
};

/**
 * The reader confirmed Discard on a non-empty selection, throwing it away.
 *
 * Only the deliberate discard, not every exit: leaving with an empty list has
 * lost nothing, and the exit that follows a successful submit is the flow
 * working. How far the reader had got is read from whether
 * `BulkEnquiryReviewOpened` / `BulkEnquiryFormOpened` fired earlier in the
 * session, so it needs no field of its own.
 */
export const trackBulkEnquiryDiscarded = (
    tracker: Tracker,
    { itemCount, supplierCount }: { itemCount: number; supplierCount: number },
): void =>
    logBulkEvent('BulkEnquiryDiscarded', tracker, {
        itemCount,
        supplierCount,
    });
